import { motion } from "framer-motion";
import { ArrowUpRight, Anchor, BookMarked, LineChart, Stethoscope, HeartPulse, Map } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "../../lib/motion";

const PRODUCTS = [
  {
    id: "bosuns-mate",
    name: "Bosun's Mate\u2122",
    tag: "Flagship",
    desc: "A maritime maintenance and navigation suite for vessels, crews, and schedules.",
    img: "https://images.unsplash.com/photo-1540946485063-a40da27545f8",
    icon: Anchor,
    href: "https://bosunsmate.app",
    hrefLabel: "bosunsmate.app",
    span: "md:col-span-7 md:row-span-2",
    status: "live",
  },
  {
    id: "tomekeeper",
    name: "TomeKeeper\u2122",
    tag: "EPUB Library",
    desc: "Professional EPUB library management for serious collections.",
    img: "https://images.unsplash.com/photo-1535905748047-14b2415c77d5",
    icon: BookMarked,
    span: "md:col-span-5",
    status: "soon",
  },
  {
    id: "bell-health",
    name: "Bell Health",
    tag: "Health Services & Technology",
    desc: "Home of the EMS Field Guide today, with Heart & Stroke Canada CPR Courses coming soon.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d",
    icon: Stethoscope,
    href: "https://bellhealth.ca/welcome",
    hrefLabel: "bellhealth.ca",
    span: "md:col-span-5",
    status: "live",
  },
  {
    id: "yems",
    name: "EMS Field Guide",
    tag: "Clinical Protocol",
    desc: "Specialized clinical protocol navigation for paramedicine and field medicine.",
    img: "https://images.unsplash.com/photo-1589739900593-8b1b925ee197",
    icon: HeartPulse,
    href: "https://bellhealth.ca/welcome",
    hrefLabel: "bellhealth.ca",
    span: "md:col-span-7",
    status: "live",
  },
  {
    id: "tradewinds",
    name: "Tradewinds\u2122",
    tag: "Trading Infra",
    desc: "Advanced digital trading infrastructure built for speed and resilience.",
    img: "https://images.unsplash.com/photo-1640458240527-6f27eabe8368",
    icon: LineChart,
    span: "md:col-span-5",
    status: "soon",
  },
  {
    id: "tide-atlas",
    name: "Tide Atlas",
    tag: "Cartography",
    desc: "A cartographic project in active development — details to come.",
    img: "https://images.unsplash.com/photo-1524661135-423995f22d0b",
    icon: Map,
    span: "md:col-span-12",
    status: "development",
  },
];

const ProductCard = ({ p, large }) => {
  const Icon = p.icon;
  const isExternal = Boolean(p.href);
  const Wrapper = isExternal ? "a" : "div";
  const wrapperProps = isExternal
    ? { href: p.href, target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <motion.div variants={fadeUp} className={`${p.span} min-h-[300px]`}>
      <Wrapper
        {...wrapperProps}
        data-testid={`product-card-${p.id}`}
        className={`group relative flex h-full flex-col overflow-hidden rounded-sm border border-[#0C2340]/10 bg-white shadow-[0_10px_34px_rgba(12,35,64,0.10)] transition-all duration-500 hover:border-[#0C2340]/25 hover:shadow-[0_18px_48px_rgba(12,35,64,0.16)] ${
          isExternal ? "cursor-pointer" : ""
        }`}
      >
        {/* Image */}
        <div className={`relative overflow-hidden ${large ? "h-72 md:h-96" : "h-52"}`}>
          <img src={p.img} alt={p.name} className="img-card h-full w-full object-cover" />
          {p.status === "soon" ? (
            <span
              className="font-mono-2 absolute right-4 top-4 rounded-full border border-white/60 bg-white/85 px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-[#0C2340] backdrop-blur"
              data-testid={`product-badge-${p.id}`}
            >
              Coming Soon
            </span>
          ) : p.status === "development" ? (
            <span
              className="font-mono-2 absolute right-4 top-4 flex items-center gap-2 rounded-full border border-white/60 bg-white/85 px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-[#0C2340] backdrop-blur"
              data-testid={`product-badge-${p.id}`}
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#0C2340]" />
              Under Development
            </span>
          ) : isExternal ? (
            <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/60 bg-white/85 text-[#0C2340] backdrop-blur transition-all duration-500 group-hover:bg-[#0C2340] group-hover:text-white">
              <ArrowUpRight size={16} />
            </span>
          ) : null}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-7">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/[0.04] text-[#0C2340]">
              <Icon size={18} strokeWidth={1.5} />
            </span>
            <span className="overline overline-dark">/ {p.tag}</span>
          </div>

          <h3 className={`font-display tracking-tight text-[#0C2340] ${large ? "mt-6 text-4xl md:text-5xl" : "mt-5 text-2xl md:text-3xl"}`}>
            {p.name}
          </h3>
          <p className={`mt-3 font-body leading-relaxed text-[#1E3A5F] ${large ? "max-w-md text-base md:text-lg" : "text-sm md:text-base"}`}>
            {p.desc}
          </p>
          {isExternal && (
            <span className="font-mono-2 mt-auto pt-5 text-[0.7rem] uppercase tracking-[0.2em] text-[#2E4A68] transition-colors group-hover:text-[#0C2340]">
              {p.hrefLabel || p.href} &rarr;
            </span>
          )}
        </div>
      </Wrapper>
    </motion.div>
  );
};

export const Fleet = () => {
  return (
    <section id="fleet" className="section-pad relative" data-testid="fleet-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <motion.div variants={fadeUp}>
            <p className="overline overline-dark mb-5">/ Product Portfolio</p>
            <h2 className="font-display text-5xl tracking-tight text-[#0C2340] md:text-7xl">
              The <span>Fleet</span>
            </h2>
          </motion.div>
          <motion.p variants={fadeUp} className="max-w-sm font-body text-base leading-relaxed text-[#1E3A5F]">
            Purpose-built products, each designed for a distinct industry with
            precision and care.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-6 md:grid-cols-12"
        >
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} p={p} large={p.id === "bosuns-mate"} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
